import React from 'react';
import FeatureShowcase from '../../components/ui/FeatureShowcase';
import { Shield, FileCheck, AlertCircle, Clock, Calendar } from 'lucide-react';

const ComplianceVisual: React.FC = () => (
  <div className="aspect-[4/3] bg-gradient-to-br from-purple-50 to-violet-100 p-4 sm:p-6 lg:p-8 flex items-center justify-center relative overflow-hidden">
    {/* Dashboard Card */}
    <div className="bg-white rounded-2xl shadow-xl p-4 sm:p-6 w-full max-w-sm sm:max-w-md relative z-10">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
            <Shield className="w-6 h-6 text-purple-600" />
          </div>
          <div>
            <h3 className="font-semibold text-navy-950">Compliance Hub</h3>
            <p className="text-xs text-gray-400">All documents in one place</p>
          </div>
        </div>
        <div className="text-right">
          <div className="text-2xl font-bold text-accent-green">98%</div>
          <div className="text-xs text-gray-400">Compliant</div>
        </div>
      </div>

      {/* Document List */}
      <div className="space-y-3">
        {/* Expiring Soon */}
        <div className="flex items-center justify-between p-3 bg-orange-50 rounded-lg border border-orange-100">
          <div className="flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-accent-orange" />
            <div>
              <div className="text-sm font-medium text-navy-950">Vehicle Insurance</div>
              <div className="text-xs text-accent-orange">Expires in 7 days</div>
            </div>
          </div>
          <button className="text-xs font-semibold text-accent-orange hover:underline">
            Renew
          </button>
        </div>

        {/* Valid Documents */}
        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
          <div className="flex items-center gap-3">
            <FileCheck className="w-5 h-5 text-accent-green" />
            <div>
              <div className="text-sm font-medium text-navy-950">Fitness Certificate</div>
              <div className="text-xs text-gray-400">Valid until Mar 2026</div>
            </div>
          </div>
          <span className="text-xs text-accent-green font-medium">Active</span>
        </div>

        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
          <div className="flex items-center gap-3">
            <FileCheck className="w-5 h-5 text-accent-green" />
            <div>
              <div className="text-sm font-medium text-navy-950">Pollution Certificate</div>
              <div className="text-xs text-gray-400">Valid until Jun 2026</div>
            </div>
          </div>
          <span className="text-xs text-accent-green font-medium">Active</span>
        </div>

        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
          <div className="flex items-center gap-3">
            <FileCheck className="w-5 h-5 text-accent-green" />
            <div>
              <div className="text-sm font-medium text-navy-950">Driver License</div>
              <div className="text-xs text-gray-400">Valid until Dec 2027</div>
            </div>
          </div>
          <span className="text-xs text-accent-green font-medium">Active</span>
        </div>
      </div>

      {/* Footer */}
      <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-between text-xs text-gray-500">
        <div className="flex items-center gap-1">
          <Clock className="w-3 h-3" />
          <span>Next renewal: Feb 18</span>
        </div>
        <span>142 documents stored</span>
      </div>
    </div>

    {/* Notification Badge */}
    <div className="absolute top-4 left-4 sm:top-6 sm:left-6 lg:top-8 lg:left-8 bg-white rounded-xl shadow-sm p-3 sm:p-4 animate-pulse z-20">
      <div className="flex items-center gap-2">
        <Calendar className="w-4 h-4 sm:w-5 sm:h-5 text-accent-orange" />
        <div>
          <div className="text-xs font-semibold text-navy-950">Reminder Set</div>
          <div className="text-[10px] sm:text-xs text-gray-400">30 days before expiry</div>
        </div>
      </div>
    </div>

    {/* Background Elements */}
    <div className="absolute top-1/4 right-1/4 w-24 h-24 sm:w-32 sm:h-32 border border-purple-200 rounded-full opacity-50" />
    <div className="absolute bottom-1/4 left-1/4 w-16 h-16 sm:w-24 sm:h-24 border border-purple-200 rounded-full opacity-30" />
  </div>
);

const FeatureCompliance: React.FC = () => {
  return (
    <FeatureShowcase
      id="compliance"
      eyebrow="Compliance Management"
      title="Never miss a"
      highlight="deadline again"
      description="Automated reminders for vehicle licenses, insurance, pollution certificates, and fitness checks. Digital document storage with renewal tracking keeps your fleet legally compliant 24/7."
      capabilities={[
        "Auto reminders for all documents",
        "Digital document vault",
        "Compliance reporting",
        "Renewal tracking & alerts"
      ]}
      visual={<ComplianceVisual />}
      stats={[
        { value: "100%", label: "Compliance" },
        { value: "0", label: "Missed Renewals" },
        { value: "50+", label: "Doc Types" }
      ]}
      ctaText="Explore Compliance Tools"
    />
  );
};

export default FeatureCompliance;
